import React, { useState, useEffect, useCallback } from "react";
import {
  Brain,
  RefreshCw,
  AlertTriangle,
  Lock,
  Video,
  BarChart2,
  Sparkles,
  Users,
  Percent,
  ScanFace,
  Shield,
} from "lucide-react";
import { toast } from "react-toastify";
import { SpinLoader } from "../../../components/loader/Loader";
import {
  getAllAccessZones,
  getUnifiedThreatDashboard,
  getRiskScores,
  calculateRiskScoreForZone,
  getPredictiveSecurityOverview,
  getPredictiveSecuritySettings,
  updatePredictiveSecuritySettings,
  UnifiedThreatZoneAgg,
  RiskScoreRecord,
  PredictiveSecurityOverview,
  PredictiveSecuritySettings,
} from "../../../service/securityService";
import type { AccessZone } from "../../../types/security_types";

const jurisdictionLabels: Record<string, string> = {
  EU_GDPR: "EU (GDPR-aligned)",
  UK_GDPR: "UK (UK GDPR / DPA)",
  US_STATE_LAW: "United States (state privacy laws)",
  OTHER: "Other / custom policy",
};

const facialStatusLabel = (status?: string) => {
  if (status === "policy_blocked") return "Restricted by jurisdiction";
  if (status === "awaiting_integration") return "Policy on — awaiting VMS / IoT";
  return "Off";
};

const PredictiveSecurityHub: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [savingPrivacy, setSavingPrivacy] = useState(false);
  const [zones, setZones] = useState<AccessZone[]>([]);
  const [riskRows, setRiskRows] = useState<RiskScoreRecord[]>([]);
  const [threatMap, setThreatMap] = useState<UnifiedThreatZoneAgg[]>([]);
  const [days, setDays] = useState(7);
  const [overview, setOverview] = useState<PredictiveSecurityOverview | null>(
    null
  );
  const [privacyForm, setPrivacyForm] = useState<PredictiveSecuritySettings | null>(
    null
  );

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [
        zList,
        scores,
        unified,
        ov,
        settingsDoc,
      ] = await Promise.all([
        getAllAccessZones(),
        getRiskScores({ limit: 100 }),
        getUnifiedThreatDashboard(days),
        getPredictiveSecurityOverview({ days }),
        getPredictiveSecuritySettings(),
      ]);
      setZones(zList);
      setRiskRows(scores.data);
      setThreatMap((unified?.threatMap as UnifiedThreatZoneAgg[]) || []);
      setOverview(ov);
      if (settingsDoc) setPrivacyForm(settingsDoc);
    } catch (e) {
      console.error(e);
      toast.error("Failed to load predictive security data");
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    load();
  }, [load]);

  const savePrivacy = async () => {
    if (!privacyForm) return;
    setSavingPrivacy(true);
    try {
      const updated = await updatePredictiveSecuritySettings({
        jurisdictionRegion: privacyForm.jurisdictionRegion,
        biometricAnalyticsEnabled: privacyForm.biometricAnalyticsEnabled,
        behaviorAnalyticsEnabled: privacyForm.behaviorAnalyticsEnabled,
        crowdAnalyticsEnabled: privacyForm.crowdAnalyticsEnabled,
        incidentPredictionEnabled: privacyForm.incidentPredictionEnabled,
        analyticsRetentionDays: privacyForm.analyticsRetentionDays,
        lawfulBasisSummary: privacyForm.lawfulBasisSummary,
      });
      if (updated) setPrivacyForm(updated);
      toast.success("Privacy settings saved");
      const ov = await getPredictiveSecurityOverview({ days });
      setOverview(ov);
    } catch {
      toast.error("Could not save privacy settings");
    } finally {
      setSavingPrivacy(false);
    }
  };

  const riskByZoneId = new Map(
    riskRows.map((r) => [String(r.zoneId || ""), r])
  );

  const recalcAll = async () => {
    if (!zones.length) {
      toast.warn("No zones to score");
      return;
    }
    setBusy(true);
    try {
      let ok = 0;
      for (const z of zones) {
        const id = z._id || z.id;
        if (!id) continue;
        try {
          await calculateRiskScoreForZone(String(id));
          ok++;
        } catch {
          /* skip invalid ids */
        }
      }
      toast.success(`Recalculated ${ok} zone(s)`);
      await load();
    } catch (e) {
      toast.error("Risk recalculation failed");
    } finally {
      setBusy(false);
    }
  };

  const recalcOne = async (zoneId: string) => {
    setBusy(true);
    try {
      await calculateRiskScoreForZone(zoneId);
      toast.success("Zone risk updated");
      await load();
    } catch {
      toast.error("Could not calculate risk for this zone");
    } finally {
      setBusy(false);
    }
  };

  const zoneRiskLevel = (zoneId: string) => {
    const r = riskByZoneId.get(zoneId);
    return r?.riskLevel || "—";
  };

  const zoneRiskScore = (zoneId: string) => {
    const r = riskByZoneId.get(zoneId);
    return r?.riskScore ?? null;
  };

  const heatClass = (score: number | null) => {
    if (score == null) return "bg-gray-200";
    if (score >= 75) return "bg-red-500";
    if (score >= 50) return "bg-orange-500";
    if (score >= 25) return "bg-amber-400";
    return "bg-emerald-500";
  };

  const sb = overview?.suspiciousBehavior;
  const crowd = overview?.crowdAnomaly;
  const incProb = overview?.incidentProbability;
  const fr = overview?.facialRecognition;

  return (
    <div className="px-2 py-4 w-full h-full mt-10 space-y-6">
      <div className="flex flex-wrap justify-between items-start gap-4">
        <div className="flex items-center gap-3">
          <div className="bg-[#EAFBFA] p-3 rounded-lg">
            <Brain className="w-7 h-7 text-[#0fa9a6]" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              AI {"&"} Predictive Security (Advanced)
            </h1>
            <p className="text-gray-600 text-sm max-w-2xl">
              Zone risk scores use incidents and access violations. Predictive
              signals below are jurisdiction-aware, rule-based indicators from the
              same operational data—useful for triage until IoT, VMS, or ML
              services are connected.
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <label className="text-sm text-gray-600 flex items-center gap-2">
            Analysis period
            <select
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
              className="border rounded-lg px-2 py-1 text-sm"
            >
              {[7, 14, 30].map((d) => (
                <option key={d} value={d}>
                  {d}d
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            disabled={busy}
            onClick={() => load()}
            className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-full text-sm hover:bg-gray-50 disabled:opacity-50"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={recalcAll}
            className="inline-flex items-center gap-2 px-4 py-2 bg-[#0fa9a6] text-white rounded-full text-sm hover:bg-teal-600 disabled:opacity-50"
          >
            <BarChart2 className="w-4 h-4" />
            Recalculate all zones
          </button>
        </div>
      </div>

      <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
        <strong>Privacy:</strong> Set jurisdiction and lawful basis before enabling
        biometric-class analytics. Retention and opt-outs should follow your
        GDPR or local privacy program.
      </div>

      {privacyForm && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5 space-y-4">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Lock className="w-5 h-5 text-gray-600" />
            Jurisdiction & analytics controls
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <label className="flex flex-col gap-1">
              <span className="text-gray-600">Jurisdiction profile</span>
              <select
                className="border rounded-lg px-3 py-2"
                value={privacyForm.jurisdictionRegion}
                onChange={(e) =>
                  setPrivacyForm({
                    ...privacyForm,
                    jurisdictionRegion: e.target
                      .value as PredictiveSecuritySettings["jurisdictionRegion"],
                  })
                }
              >
                {Object.entries(jurisdictionLabels).map(([k, v]) => (
                  <option key={k} value={k}>
                    {v}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-gray-600">Analytics retention (days)</span>
              <input
                type="number"
                min={1}
                max={3650}
                className="border rounded-lg px-3 py-2"
                value={privacyForm.analyticsRetentionDays}
                onChange={(e) =>
                  setPrivacyForm({
                    ...privacyForm,
                    analyticsRetentionDays: Number(e.target.value) || 90,
                  })
                }
              />
            </label>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            {[
              {
                key: "behaviorAnalyticsEnabled" as const,
                label: "Suspicious behavior heuristics",
              },
              {
                key: "crowdAnalyticsEnabled" as const,
                label: "Crowd / traffic proxy analytics",
              },
              {
                key: "incidentPredictionEnabled" as const,
                label: "Incident probability (historical rate)",
              },
              {
                key: "biometricAnalyticsEnabled" as const,
                label: "Biometric / facial analytics (legal gate only)",
              },
            ].map((row) => (
              <label
                key={row.key}
                className="flex items-center gap-2 cursor-pointer"
              >
                <input
                  type="checkbox"
                  className="rounded border-gray-300"
                  checked={!!privacyForm[row.key]}
                  onChange={(e) =>
                    setPrivacyForm({
                      ...privacyForm,
                      [row.key]: e.target.checked,
                    })
                  }
                />
                <span>{row.label}</span>
              </label>
            ))}
          </div>
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-gray-600">
              Lawful basis summary (optional, for audit notes)
            </span>
            <textarea
              className="border rounded-lg px-3 py-2 min-h-[72px]"
              maxLength={500}
              placeholder="e.g. Legitimate interest for site security; DPIA ref …"
              value={privacyForm.lawfulBasisSummary || ""}
              onChange={(e) =>
                setPrivacyForm({
                  ...privacyForm,
                  lawfulBasisSummary: e.target.value,
                })
              }
            />
          </label>
          <button
            type="button"
            disabled={savingPrivacy}
            onClick={savePrivacy}
            className="px-4 py-2 bg-gray-900 text-white rounded-lg text-sm hover:bg-gray-800 disabled:opacity-50"
          >
            {savingPrivacy ? "Saving…" : "Save privacy settings"}
          </button>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-20">
          <SpinLoader />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-lg shadow-sm p-5">
              <h2 className="text-lg font-semibold text-gray-900 mb-2">
                Risk scoring by zone
              </h2>
              <p className="text-xs text-gray-500 mb-4">
                Stored scores from the security risk engine. Use recalculate to
                refresh each zone from incidents and access denials.
              </p>
              <div className="overflow-x-auto max-h-[380px] overflow-y-auto text-sm">
                <table className="w-full text-left">
                  <thead className="sticky top-0 bg-gray-100 text-xs">
                    <tr>
                      <th className="p-2">Zone</th>
                      <th className="p-2">Level</th>
                      <th className="p-2">Score</th>
                      <th className="p-2 w-28"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {zones.map((z) => {
                      const id = String(z._id || z.id || "");
                      const rs = zoneRiskScore(id);
                      return (
                        <tr key={id || z.zoneName} className="border-t">
                          <td className="p-2 font-medium">{z.zoneName}</td>
                          <td className="p-2">{zoneRiskLevel(id)}</td>
                          <td className="p-2">
                            {rs != null ? Math.round(rs) : "—"}
                          </td>
                          <td className="p-2">
                            {id && (
                              <button
                                type="button"
                                disabled={busy}
                                onClick={() => recalcOne(id)}
                                className="text-xs text-[#0fa9a6] hover:underline disabled:opacity-50"
                              >
                                Recalc
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {zones.length === 0 && (
                  <p className="text-gray-500 p-4 text-sm">
                    No access zones defined.
                  </p>
                )}
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-5">
              <h2 className="text-lg font-semibold text-gray-900 mb-2">
                Threat heatmap (unified scores)
              </h2>
              <p className="text-xs text-gray-500 mb-4">
                Combined physical and digital signals for the selected period.
                Higher bars indicate more activity in the unified model.
              </p>
              {!threatMap.length ? (
                <p className="text-gray-500 text-sm">No data for this period.</p>
              ) : (
                <div className="space-y-3 max-h-[380px] overflow-y-auto">
                  {threatMap.map((z) => (
                    <div key={z.location}>
                      <div className="flex justify-between text-xs mb-1">
                        <span className="truncate max-w-[65%] font-medium">
                          {z.location}
                        </span>
                        <span className="text-gray-500">
                          {Math.round(z.threatScore)}
                        </span>
                      </div>
                      <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full ${heatClass(
                            z.threatScore
                          )}`}
                          style={{ width: `${Math.min(100, z.threatScore)}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-violet-500" />
              Predictive indicators ({overview?.evaluationPeriodDays ?? days}d
              window)
            </h2>
            <p className="text-sm text-gray-600 mb-4">
              Rule-based analytics on incidents and access logs. External ML, VMS,
              and IoT feeds can replace or augment these signals later.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-white rounded-lg border border-gray-200 p-4 flex gap-3">
                <div className="p-2 rounded-lg bg-violet-50 h-fit">
                  <Shield className="w-5 h-5 text-violet-600" />
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="font-semibold text-gray-900 text-sm">
                    Suspicious behavior detection
                  </h3>
                  <p className="text-xs text-gray-500 mt-1">
                    Denials and severe incidents vs the prior period (
                    {sb?.methodology === "rules_engine"
                      ? "rules engine"
                      : "—"}
                    ).
                  </p>
                  {sb?.enabled ? (
                    <p className="text-sm mt-2 font-medium text-gray-800">
                      Score {sb.score ?? "—"} · {sb.level ?? "—"}
                    </p>
                  ) : (
                    <p className="text-sm mt-2 text-gray-500">Disabled in settings</p>
                  )}
                  <ul className="mt-2 text-xs text-gray-600 list-disc pl-4 space-y-1">
                    {(sb?.signals || []).slice(0, 4).map((s, i) => (
                      <li key={i}>{s}</li>
                    ))}
                  </ul>
                  <span
                    className={`inline-block mt-2 text-xs font-medium px-2 py-0.5 rounded ${
                      sb?.enabled
                        ? "text-teal-800 bg-teal-50"
                        : "text-gray-500 bg-gray-100"
                    }`}
                  >
                    {sb?.enabled ? "Active (heuristic)" : "Off"}
                  </span>
                </div>
              </div>

              <div className="bg-white rounded-lg border border-gray-200 p-4 flex gap-3">
                <div className="p-2 rounded-lg bg-sky-50 h-fit">
                  <Users className="w-5 h-5 text-sky-600" />
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="font-semibold text-gray-900 text-sm">
                    Crowd anomaly detection
                  </h3>
                  <p className="text-xs text-gray-500 mt-1">
                    Proxy from granted access volume (last 24h vs previous 24h).
                    Not a people-count without sensors or video.
                  </p>
                  {crowd?.enabled ? (
                    <p className="text-sm mt-2 font-medium text-gray-800">
                      Score {crowd.score ?? "—"} · {crowd.level ?? "—"}
                    </p>
                  ) : (
                    <p className="text-sm mt-2 text-gray-500">Disabled in settings</p>
                  )}
                  <ul className="mt-2 text-xs text-gray-600 list-disc pl-4 space-y-1">
                    {(crowd?.signals || []).slice(0, 4).map((s, i) => (
                      <li key={i}>{s}</li>
                    ))}
                  </ul>
                  <span
                    className={`inline-block mt-2 text-xs font-medium px-2 py-0.5 rounded ${
                      crowd?.enabled
                        ? "text-teal-800 bg-teal-50"
                        : "text-gray-500 bg-gray-100"
                    }`}
                  >
                    {crowd?.enabled ? "Active (proxy)" : "Off"}
                  </span>
                </div>
              </div>

              <div className="bg-white rounded-lg border border-gray-200 p-4 flex gap-3">
                <div className="p-2 rounded-lg bg-amber-50 h-fit">
                  <Percent className="w-5 h-5 text-amber-700" />
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="font-semibold text-gray-900 text-sm">
                    Incident probability prediction
                  </h3>
                  <p className="text-xs text-gray-500 mt-1">
                    Tenant-wide and per-zone likelihood from recent vs
                    historical incident rates (~{incProb?.horizonHours ?? 24}h
                    horizon).
                  </p>
                  {incProb?.enabled ? (
                    <>
                      <p className="text-sm mt-2 font-medium text-gray-800">
                        Tenant {incProb.tenantScore ?? "—"} ·{" "}
                        {incProb.tenantLevel ?? "—"}
                      </p>
                      <div className="mt-2 text-xs text-gray-600 space-y-1">
                        {(incProb.byZone || []).slice(0, 5).map((z) => (
                          <div
                            key={z.zoneId}
                            className="flex justify-between gap-2 border-t border-gray-100 pt-1 first:border-0 first:pt-0"
                          >
                            <span className="truncate">{z.zoneName}</span>
                            <span>
                              {z.probability}% ({z.level})
                            </span>
                          </div>
                        ))}
                        {!incProb.byZone?.length && (
                          <span>No zoned incidents to compare.</span>
                        )}
                      </div>
                    </>
                  ) : (
                    <p className="text-sm mt-2 text-gray-500">Disabled in settings</p>
                  )}
                  <span
                    className={`inline-block mt-2 text-xs font-medium px-2 py-0.5 rounded ${
                      incProb?.enabled
                        ? "text-teal-800 bg-teal-50"
                        : "text-gray-500 bg-gray-100"
                    }`}
                  >
                    {incProb?.enabled ? "Active (historical)" : "Off"}
                  </span>
                </div>
              </div>

              <div className="bg-white rounded-lg border border-gray-200 p-4 flex gap-3">
                <div className="p-2 rounded-lg bg-gray-100 h-fit">
                  <ScanFace className="w-5 h-5 text-gray-600" />
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="font-semibold text-gray-900 text-sm">
                    Facial recognition (where legally permitted)
                  </h3>
                  <p className="text-xs text-gray-600 mt-1">{fr?.detail}</p>
                  <span className="inline-block mt-2 text-xs font-medium text-indigo-800 bg-indigo-50 px-2 py-0.5 rounded">
                    {facialStatusLabel(fr?.operationalStatus)}
                  </span>
                  {fr?.biometricOptIn && (
                    <p className="text-xs text-gray-500 mt-1">
                      Biometric-class analytics enabled in policy—connect VMS or
                      IoT to perform recognition.
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {overview?.disclaimers && overview.disclaimers.length > 0 && (
            <div className="rounded-lg bg-gray-50 border border-gray-200 px-4 py-3 text-xs text-gray-600 space-y-1">
              {overview.disclaimers.map((d, i) => (
                <p key={i}>{d}</p>
              ))}
            </div>
          )}

          <div className="flex flex-wrap gap-4 text-xs text-gray-500">
            <span className="flex items-center gap-1">
              <Lock className="w-3 h-3" /> Jurisdiction controls above apply to
              displayed analytics
            </span>
            <span className="flex items-center gap-1">
              <Video className="w-3 h-3" /> VMS / IoT integration is separate
            </span>
            <span className="flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" /> Not legal advice—align with
              counsel
            </span>
          </div>
        </>
      )}
    </div>
  );
};

export default PredictiveSecurityHub;
